import { useEffect, useMemo, useState } from 'react';
import Drawer from '@/components/base/Drawer';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { SERVICES } from '@/mocks/constants';
import type { Quote } from '@/types';
import { toTrDate, parseTrDate } from '@/lib/date';

const inputClass =
  'h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const labelClass = 'mb-1.5 block text-xs font-semibold text-foreground-600';

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface QuoteBuilderProps {
  open: boolean;
  onClose: () => void;
  prefillCustomer?: string;
  prefillService?: string;
  prefillLocation?: string;
}

export default function QuoteBuilder({
  open,
  onClose,
  prefillCustomer,
  prefillService,
  prefillLocation,
}: QuoteBuilderProps) {
  const { customers, addQuote } = useAppStore();
  const [customerName, setCustomerName] = useState(prefillCustomer ?? '');
  const [service, setService] = useState(prefillService ?? SERVICES[0]);
  const [location, setLocation] = useState(prefillLocation ?? '');
  const [scope, setScope] = useState('');
  const [date, setDate] = useState(toTrDate(new Date()));
  const [operationalNote, setOperationalNote] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  const dateIso = useMemo(() => {
    const d = parseTrDate(date);
    return toIsoDate(d);
  }, [date]);

  const quoteNo = useMemo(() => `VP-2026-${String(Date.now()).slice(-4)}`, []);

  function reset() {
    setCustomerName(prefillCustomer ?? '');
    setService(prefillService ?? SERVICES[0]);
    setLocation(prefillLocation ?? '');
    setScope('');
    setDate(toTrDate(new Date()));
    setOperationalNote('');
    setCustomerNote('');
    setAmount('');
    setError('');
  }

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillCustomer, prefillService, prefillLocation]);

  function buildQuote(status: Quote['status']): Quote {
    return {
      id: quoteNo,
      customerName,
      service,
      location,
      date,
      amount: Number(amount) || 0,
      status,
      revision: 1,
      scope,
      operationalNote,
      customerNote,
    };
  }

  function save(status: Quote['status']) {
    if (!customerName || !location) {
      setError('Müşteri ve konum zorunludur.');
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError('Geçerli bir hizmet bedeli girilmelidir.');
      return;
    }
    addQuote(buildQuote(status));
    onClose();
  }

  const numericAmount = Number(amount) || 0;

  return (
    <Drawer open={open} onClose={onClose} title="Teklif Oluştur" width="max-w-3xl">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Müşteri</label>
            <select className={inputClass} value={customerName} onChange={(e) => setCustomerName(e.target.value)}>
              <option value="">Müşteri seç</option>
              {customers.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Hizmet</label>
            <select className={inputClass} value={service} onChange={(e) => setService(e.target.value)}>
              {SERVICES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Konum</label>
            <input className={inputClass} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Beykoz / İstanbul" />
          </div>
          <div>
            <label className={labelClass}>Temizlik Kapsamı</label>
            <textarea
              className="h-20 w-full resize-none rounded-md border border-background-200 bg-white px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="Yapılacak işin kapsamı"
            />
          </div>
          <div>
            <label className={labelClass}>Önerilen Tarih</label>
            <input
              type="date"
              className={inputClass}
              value={dateIso}
              onChange={(e) => {
                const iso = e.target.value;
                if (iso) {
                  const d = new Date(iso);
                  setDate(toTrDate(d));
                }
              }}
            />
          </div>
          <div>
            <label className={labelClass}>Hizmet Bedeli (TL)</label>
            <input
              className={inputClass}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="18500"
            />
            <p className="mt-1 text-xs text-foreground-400">İşe özel toplam bedel — saatlik paket kullanılmaz.</p>
          </div>
          <div>
            <label className={labelClass}>Dahili Operasyon Notu</label>
            <textarea
              className="h-16 w-full resize-none rounded-md border border-background-200 bg-white px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              value={operationalNote}
              onChange={(e) => setOperationalNote(e.target.value)}
              placeholder="Ekip ve operasyon için not"
            />
          </div>
          <div>
            <label className={labelClass}>Müşteriye Görünen Not</label>
            <textarea
              className="h-16 w-full resize-none rounded-md border border-background-200 bg-white px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              value={customerNote}
              onChange={(e) => setCustomerNote(e.target.value)}
              placeholder="Müşterinin göreceği açıklama"
            />
          </div>
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>}
        </div>

        {/* Live preview */}
        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex items-center justify-between border-b-2 border-primary-950 pb-3">
            <div>
              <div className="font-heading text-sm font-extrabold tracking-tight text-primary-950">VİAYPİİ</div>
              <div className="text-[10px] font-semibold tracking-[0.2em] text-accent-600">TEMİZLİK</div>
            </div>
            <div className="text-right text-[11px] text-foreground-500">
              <div>Teklif No</div>
              <div className="font-semibold text-foreground-800">{quoteNo}</div>
            </div>
          </div>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-foreground-500">Müşteri</span>
              <span className="font-semibold text-foreground-900">{customerName || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-500">Hizmet</span>
              <span className="font-semibold text-foreground-900">{service}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-500">Konum</span>
              <span className="font-semibold text-foreground-900">{location || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-500">Tarih</span>
              <span className="font-semibold text-foreground-900">{date}</span>
            </div>
          </div>
          {scope && <p className="mt-3 rounded-md bg-background-50 p-3 text-xs text-foreground-600">{scope}</p>}
          <div className="mt-4 flex items-center justify-between rounded-lg bg-primary-950 px-4 py-3">
            <span className="text-sm font-semibold text-primary-200">Toplam</span>
            <span className="font-heading text-lg font-bold text-accent-400">{formatTL(numericAmount)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-background-100 pt-4">
        <button
          type="button"
          onClick={() => save('Taslak')}
          className="rounded-md border border-background-200 px-4 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
        >
          Taslağı Kaydet
        </button>
        <button
          type="button"
          onClick={() => save('Hazırlandı')}
          className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
        >
          Teklifi Hazırla
        </button>
      </div>
    </Drawer>
  );
}